/**
 * Count Down component to count down the time
 * Author: Velu S Gautam <velu.gautam@comeon.com>
 */

import { useState, useEffect } from 'react';
/**
 * Get Time Left
 * @param {Date|string|number} countDownDate
 */
const getTimeLeft = countDownDate => {
	let startTimestamp;

	// If string Date converting to milliseconds
	if (typeof countDownDate === 'string') {
		startTimestamp = new Date(countDownDate).getTime();
	} else if (countDownDate instanceof Date) {
		// getting milliseconds from Date object
		startTimestamp = countDownDate.getTime();
	} else {
		// setting the date in milliseconds
		startTimestamp = countDownDate;
	}

	// getting the date into total seconds for last second precision
	const total = Math.round(parseFloat((Math.max(0, startTimestamp - Date.now()) / 1000).toFixed(0)) * 1000);

	// if total is NaN then the time format has error
	if (Number.isNaN(total)) {
		// eslint-disable-next-line no-console
		console.error('Error in date format passed to Count Down Component: %o', total);
		return { completed: true };
	}

	const seconds = total / 1000;

	return {
		total,
		days: Math.floor(seconds / (3600 * 24)),
		hours: Math.floor((seconds / 3600) % 24),
		minutes: Math.floor((seconds / 60) % 60),
		seconds: Math.floor(seconds % 60),
		milliseconds: total,
		completed: total <= 0
	};
};
/**
 * Count Down Render Prop Component
 * @param {object} props - Count Down props
 * @param {Date|string|number} props.date - Starting Date for count down
 * @param {function} props.renderer - Render function
 * @param {function} [({hours, minutes, seconds}) => return seconds] props.updateFrequency - Update frequency function
 * @returns {object} [{total, hours, hours, minutes, seconds, milliseconds, completed}]
 */
const CountDown = ({ date, renderer, updateFrequency }) => {
	if (typeof renderer !== 'function') {
		throw new TypeError('Renderer Function is Expected');
	}
	const timingObject = getTimeLeft(date);
	const [, setCounter] = useState(timingObject);

	// setting default update time to 1 seconds
	let updateTimer = 1000;

	if (typeof updateFrequency === 'function') {
		// if updateFrequency is there then passing the whole counter object to get the new updateFrequency
		updateTimer = updateFrequency(timingObject);
	}

	useEffect(() => {
		if (!timingObject.completed) {
			const interval = setInterval(() => {
				setCounter(getTimeLeft(date));
			}, updateTimer);
			// return function will clear the interval before next counter
			return () => clearInterval(interval);
		}
	}, [date, updateTimer, timingObject.completed]);

	// counter value is passed to the renderer function
	return renderer(timingObject);
};

export default CountDown;
